<script setup>
// "Next step" of a step: rows that read "when <condition> go to <step>". A row's action is one of the three the
// script knows: go to a step (set), go to a step and come back after it (push), or come back to where it was
// (return). The condition is text; the chips under the rows are the conditions the catalog offers, and one click
// writes it into the row that has the focus (the last one when none has).
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { Add16, Close16 } from '@carbon/icons-vue';
import { Button } from 'dashboard/components-next/polaris/ui/button';
import { Input } from 'dashboard/components-next/polaris/ui/input';
import OptionSelect from 'dashboard/components-next/polaris/cards/OptionSelect.vue';
import { NEXT_KINDS, newRowId, predicateChips } from './scenarioStep';

const props = defineProps({
  // [{ value: node_key, label: title }]: the steps one can go to
  steps: { type: Array, required: true },
  predicates: { type: Array, default: () => [] },
  invalid: { type: Boolean, default: false },
});
const rows = defineModel({ type: Array, default: () => [] });
const { t } = useI18n();

const kinds = computed(() =>
  NEXT_KINDS.map(kind => ({
    value: kind,
    label: t(
      `POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.NEXT.KIND.${kind.toUpperCase()}`
    ),
  }))
);
const chips = computed(() => predicateChips(props.predicates));
const focused = ref(null);

const patch = (index, change) => {
  rows.value = rows.value.map((row, at) =>
    at === index ? { ...row, ...change } : row
  );
};
const remove = index => {
  rows.value = rows.value.filter((_, at) => at !== index);
};
const add = () => {
  rows.value = [
    ...rows.value,
    { uid: newRowId(), from: null, when: '', kind: 'set', target: '' },
  ];
};
const useChip = chip => {
  const index = focused.value ?? rows.value.length - 1;
  if (index < 0) {
    rows.value = [
      { uid: newRowId(), from: null, when: chip.text, kind: 'set', target: '' },
    ];
    return;
  }
  const current = rows.value[index].when.trim();
  patch(index, { when: current ? `${current} ${chip.text}` : chip.text });
};
</script>

<template>
  <div class="grid gap-3" data-next-rows>
    <div
      v-for="(row, index) in rows"
      :key="row.uid"
      class="grid gap-2 rounded-polaris-md border border-solid border-polaris-border p-3"
      data-next-row
    >
      <div class="flex items-center gap-2">
        <span
          class="shrink-0 type-polaris-caption text-polaris-muted-foreground"
        >
          {{ t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.NEXT.WHEN') }}
        </span>
        <Input
          :model-value="row.when"
          :aria-label="
            t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.NEXT.WHEN_LABEL', {
              n: index + 1,
            })
          "
          :aria-invalid="invalid || undefined"
          class="flex-1 font-mono"
          @focus="focused = index"
          @update:model-value="value => patch(index, { when: value })"
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          :aria-label="t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.REMOVE_ITEM')"
          @click="remove(index)"
        >
          <Close16 aria-hidden="true" />
        </Button>
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <OptionSelect
          :model-value="row.kind"
          :options="kinds"
          :aria-label="
            t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.NEXT.KIND_LABEL', {
              n: index + 1,
            })
          "
          size="sm"
          class="w-56"
          @update:model-value="value => patch(index, { kind: value })"
        />
        <OptionSelect
          v-if="row.kind !== 'return'"
          :model-value="row.target"
          :options="steps"
          :placeholder="t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.NEXT.PICK_STEP')"
          :aria-label="
            t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.NEXT.TARGET_LABEL', {
              n: index + 1,
            })
          "
          size="sm"
          class="min-w-48 flex-1"
          @update:model-value="value => patch(index, { target: value })"
        />
      </div>
    </div>
    <div class="flex flex-wrap items-center gap-2">
      <Button type="button" variant="ghost" size="sm" @click="add">
        <Add16 aria-hidden="true" />
        {{ t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.NEXT.ADD') }}
      </Button>
    </div>
    <div v-if="chips.length" class="grid gap-1" data-predicate-chips>
      <p class="type-polaris-caption text-polaris-muted-foreground">
        {{ t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.NEXT.CHIPS') }}
      </p>
      <div class="flex flex-wrap gap-1.5">
        <Button
          v-for="chip in chips"
          :key="chip.text"
          type="button"
          variant="outline"
          size="xs"
          class="font-mono"
          @mousedown.prevent
          @click="useChip(chip)"
        >
          {{ chip.label }}
        </Button>
      </div>
    </div>
  </div>
</template>
